import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";

/**
 * JS side of the app-local Capacitor plugins in packages/native (Android
 * only). Every caller checks `has(...)` first — on the web and iOS these
 * resolve to "not implemented", and the features fall back to what the
 * browser / WebKit offers on its own (web PiP API, AirPlay).
 */

export interface OrvisPipPlugin {
  isSupported(): Promise<{ supported: boolean }>;
  enter(opts: { width: number; height: number }): Promise<void>;
  setAutoEnter(opts: { enabled: boolean; width?: number; height?: number }): Promise<void>;
  addListener(event: "pipChanged", cb: (e: { active: boolean; dismissed?: boolean }) => void): Promise<PluginListenerHandle>;
}

export type CastState = "unavailable" | "available" | "connecting" | "connected";

export interface OrvisCastPlugin {
  isAvailable(): Promise<{ available: boolean; state: CastState; device?: string }>;
  showPicker(): Promise<void>;
  loadMedia(opts: {
    url: string;
    contentType: string;
    kind: "video" | "audio";
    title: string;
    subtitle?: string;
    image?: string;
  }): Promise<void>;
  end(): Promise<void>;
  addListener(event: "castState", cb: (e: { state: CastState; device?: string }) => void): Promise<PluginListenerHandle>;
}

export interface OrvisBackgroundAudioPlugin {
  update(opts: { title: string; subtitle?: string; artwork?: string; playing: boolean }): Promise<void>;
  stop(): Promise<void>;
  addListener(
    event: "control",
    cb: (e: { action: "play" | "pause" | "stop" | "next" | "previous" }) => void
  ): Promise<PluginListenerHandle>;
}

export interface OrvisTvChannelsPlugin {
  isTelevision(): Promise<{ television: boolean }>;
  isSupported(): Promise<{ supported: boolean }>;
  sync(opts: {
    title: string;
    items: Array<{ key: string; kind: "tv" | "radio"; name: string; description?: string; image?: string }>;
  }): Promise<void>;
}

export const OrvisPip = registerPlugin<OrvisPipPlugin>("OrvisPip");
export const OrvisCast = registerPlugin<OrvisCastPlugin>("OrvisCast");
export const OrvisBackgroundAudio = registerPlugin<OrvisBackgroundAudioPlugin>("OrvisBackgroundAudio");
export const OrvisTvChannels = registerPlugin<OrvisTvChannelsPlugin>("OrvisTvChannels");

export function hasNative(name: "OrvisPip" | "OrvisCast" | "OrvisBackgroundAudio" | "OrvisTvChannels"): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable(name);
}

/** Launch / deep-link URL a home-screen program opens: `orvis-play:<kind>/<key>`. */
export function parsePlayLink(url: string): { kind: "tv" | "radio"; key: string } | undefined {
  const m = /^orvis-play:(tv|radio)\/(.+)$/.exec(url);
  if (!m) return undefined;
  try {
    return { kind: m[1] as "tv" | "radio", key: decodeURIComponent(m[2]!) };
  } catch {
    return undefined;
  }
}
